import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db, APPLICANTS_COLLECTION } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import "./AdminDashboard.css";

const STATUS_OPTIONS = ["New", "Contacted", "Enrolled", "Declined"];

const STATUS_COLORS = {
  New: "status-new",
  Contacted: "status-contacted",
  Enrolled: "status-enrolled",
  Declined: "status-declined",
};

const formatDate = (timestamp) => {
  if (!timestamp) return "—";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }) + " " + date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isWithinLastDays = (timestamp, days) => {
  if (!timestamp) return false;
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return date >= cutoff;
};

const escapeCsvValue = (value) => {
  const str = String(value ?? "");
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const AdminDashboard = () => {
  const { currentUser, logout } = useAuth();

  const [activeTab, setActiveTab] = useState("overview");
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [courseFilter, setCourseFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    const q = query(
      collection(db, APPLICANTS_COLLECTION),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const rows = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setApplicants(rows);
        setLoading(false);
        setLoadError("");
      },
      (err) => {
        console.error("Failed to load applicants:", err);
        setLoadError(
          "Unable to load applicants. Please check your Firestore setup/permissions."
        );
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const courseList = useMemo(() => {
    const set = new Set(applicants.map((a) => a.course).filter(Boolean));
    return ["All", ...Array.from(set).sort()];
  }, [applicants]);

  const filteredApplicants = useMemo(() => {
    return applicants.filter((a) => {
      const matchesSearch =
        !searchTerm ||
        [a.fullName, a.email, a.phone]
          .filter(Boolean)
          .some((field) =>
            field.toLowerCase().includes(searchTerm.toLowerCase())
          );
      const matchesCourse = courseFilter === "All" || a.course === courseFilter;
      const matchesStatus =
        statusFilter === "All" || (a.status || "New") === statusFilter;
      return matchesSearch && matchesCourse && matchesStatus;
    });
  }, [applicants, searchTerm, courseFilter, statusFilter]);

  const stats = useMemo(() => {
    const total = applicants.length;
    const newThisWeek = applicants.filter((a) =>
      isWithinLastDays(a.createdAt, 7)
    ).length;
    const byStatus = STATUS_OPTIONS.reduce((acc, status) => {
      acc[status] = applicants.filter(
        (a) => (a.status || "New") === status
      ).length;
      return acc;
    }, {});

    const courseCounts = {};
    applicants.forEach((a) => {
      if (!a.course) return;
      courseCounts[a.course] = (courseCounts[a.course] || 0) + 1;
    });
    const topCourses = Object.entries(courseCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);

    return { total, newThisWeek, byStatus, topCourses };
  }, [applicants]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateDoc(doc(db, APPLICANTS_COLLECTION, id), {
        status: newStatus,
      });
    } catch (err) {
      console.error("Failed to update status:", err);
      window.alert("Failed to update status. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Delete this applicant record? This cannot be undone."
    );
    if (!confirmed) return;
    try {
      await deleteDoc(doc(db, APPLICANTS_COLLECTION, id));
      if (selectedApplicant?.id === id) setSelectedApplicant(null);
    } catch (err) {
      console.error("Failed to delete applicant:", err);
      window.alert("Failed to delete record. Please try again.");
    }
  };

  const openDetails = (applicant) => {
    setSelectedApplicant(applicant);
    setNotesDraft(applicant.adminNotes || "");
  };

  const saveNotes = async () => {
    if (!selectedApplicant) return;
    setSavingNotes(true);
    try {
      await updateDoc(doc(db, APPLICANTS_COLLECTION, selectedApplicant.id), {
        adminNotes: notesDraft,
      });
      setSelectedApplicant((prev) =>
        prev ? { ...prev, adminNotes: notesDraft } : prev
      );
    } catch (err) {
      console.error("Failed to save notes:", err);
      window.alert("Failed to save notes. Please try again.");
    } finally {
      setSavingNotes(false);
    }
  };

  const exportCsv = () => {
    const headers = [
      "Full Name",
      "Email",
      "Phone",
      "Course",
      "Training Mode",
      "Status",
      "Date Submitted",
      "Message",
    ];
    const rows = filteredApplicants.map((a) => [
      a.fullName,
      a.email,
      a.phone,
      a.course,
      a.trainingMode,
      a.status || "New",
      formatDate(a.createdAt),
      a.message,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `applicants-${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const maxCourseCount = stats.topCourses.length
    ? Math.max(...stats.topCourses.map(([, count]) => count))
    : 0;

  return (
    <div className="admin-dashboard">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <img src="/images/logo.png" alt="Logo" className="admin-sidebar-logo" />
          <span>Passion Care 4 U</span>
        </div>

        <nav className="admin-nav">
          <button
            className={`admin-nav-item ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            className={`admin-nav-item ${activeTab === "applicants" ? "active" : ""}`}
            onClick={() => setActiveTab("applicants")}
          >
            Applicants
            <span className="admin-nav-badge">{applicants.length}</span>
          </button>
        </nav>

        <div className="admin-sidebar-footer">
          <a href="/" className="admin-sidebar-link">
            &larr; Back to website
          </a>
        </div>
      </aside>

      <div className="admin-main">
        <header className="admin-topbar">
          <h1>
            {activeTab === "overview" ? "Overview" : "Applicants"}
          </h1>
          <div className="admin-topbar-user">
            <span>{currentUser?.email}</span>
            <button className="admin-logout-btn" onClick={logout}>
              Logout
            </button>
          </div>
        </header>

        <main className="admin-content">
          {loadError && <div className="admin-form-message error">{loadError}</div>}

          {loading ? (
            <div className="admin-loading">
              <div className="admin-spinner" />
              <p>Loading applicants...</p>
            </div>
          ) : activeTab === "overview" ? (
            <>
              <section className="admin-stats-grid">
                <div className="admin-stat-card">
                  <span className="stat-label">Total Applicants</span>
                  <span className="stat-value">{stats.total}</span>
                </div>
                <div className="admin-stat-card">
                  <span className="stat-label">New This Week</span>
                  <span className="stat-value">{stats.newThisWeek}</span>
                </div>
                <div className="admin-stat-card">
                  <span className="stat-label">Enrolled</span>
                  <span className="stat-value">
                    {stats.byStatus.Enrolled || 0}
                  </span>
                </div>
                <div className="admin-stat-card">
                  <span className="stat-label">Pending Follow-up</span>
                  <span className="stat-value">
                    {(stats.byStatus.New || 0) + (stats.byStatus.Contacted || 0)}
                  </span>
                </div>
              </section>

              <section className="admin-panel">
                <h2>Applicants by Status</h2>
                <div className="status-breakdown">
                  {STATUS_OPTIONS.map((status) => (
                    <div className="status-breakdown-item" key={status}>
                      <span className={`status-pill ${STATUS_COLORS[status]}`}>
                        {status}
                      </span>
                      <span className="status-count">
                        {stats.byStatus[status] || 0}
                      </span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="admin-panel">
                <h2>Most Popular Courses</h2>
                {stats.topCourses.length === 0 ? (
                  <p className="admin-empty-text">No applications yet.</p>
                ) : (
                  <div className="course-bar-chart">
                    {stats.topCourses.map(([course, count]) => (
                      <div className="course-bar-row" key={course}>
                        <span className="course-bar-label">{course}</span>
                        <div className="course-bar-track">
                          <div
                            className="course-bar-fill"
                            style={{
                              width: `${
                                maxCourseCount
                                  ? (count / maxCourseCount) * 100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                        <span className="course-bar-count">{count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          ) : (
            <>
              <section className="admin-filters">
                <input
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="admin-search-input"
                />
                <select
                  value={courseFilter}
                  onChange={(e) => setCourseFilter(e.target.value)}
                >
                  {courseList.map((course) => (
                    <option key={course} value={course}>
                      {course}
                    </option>
                  ))}
                </select>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="All">All Statuses</option>
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                <button className="btn btn-secondary" onClick={exportCsv}>
                  Export CSV
                </button>
              </section>

              <section className="admin-panel admin-table-panel">
                {filteredApplicants.length === 0 ? (
                  <p className="admin-empty-text">
                    No applicants match your current filters.
                  </p>
                ) : (
                  <div className="admin-table-wrapper">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Contact</th>
                          <th>Course</th>
                          <th>Mode</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredApplicants.map((a) => (
                          <tr key={a.id}>
                            <td>{a.fullName}</td>
                            <td>
                              <div className="admin-contact-cell">
                                <span>{a.email}</span>
                                <span className="admin-contact-phone">
                                  {a.phone}
                                </span>
                              </div>
                            </td>
                            <td>{a.course}</td>
                            <td>{a.trainingMode || "—"}</td>
                            <td>{formatDate(a.createdAt)}</td>
                            <td>
                              <select
                                className={`status-select ${STATUS_COLORS[a.status || "New"]}`}
                                value={a.status || "New"}
                                onChange={(e) =>
                                  handleStatusChange(a.id, e.target.value)
                                }
                              >
                                {STATUS_OPTIONS.map((status) => (
                                  <option key={status} value={status}>
                                    {status}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <div className="admin-actions">
                                <button
                                  className="admin-action-btn"
                                  onClick={() => openDetails(a)}
                                >
                                  View
                                </button>
                                <button
                                  className="admin-action-btn danger"
                                  onClick={() => handleDelete(a.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}
        </main>
      </div>

      {selectedApplicant && (
        <div className="admin-modal-overlay" onClick={() => setSelectedApplicant(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="admin-modal-close"
              onClick={() => setSelectedApplicant(null)}
              aria-label="Close"
            >
              &times;
            </button>
            <h2>{selectedApplicant.fullName}</h2>
            <span
              className={`status-pill ${STATUS_COLORS[selectedApplicant.status || "New"]}`}
            >
              {selectedApplicant.status || "New"}
            </span>

            <div className="admin-modal-grid">
              <div>
                <span className="admin-modal-label">Email</span>
                <p>{selectedApplicant.email}</p>
              </div>
              <div>
                <span className="admin-modal-label">Phone</span>
                <p>{selectedApplicant.phone}</p>
              </div>
              <div>
                <span className="admin-modal-label">Course</span>
                <p>{selectedApplicant.course}</p>
              </div>
              <div>
                <span className="admin-modal-label">Training Mode</span>
                <p>{selectedApplicant.trainingMode || "—"}</p>
              </div>
              <div>
                <span className="admin-modal-label">Submitted</span>
                <p>{formatDate(selectedApplicant.createdAt)}</p>
              </div>
            </div>

            <div className="admin-modal-message">
              <span className="admin-modal-label">Applicant Message</span>
              <p>{selectedApplicant.message || "No additional message provided."}</p>
            </div>

            <div className="admin-modal-notes">
              <span className="admin-modal-label">Internal Admin Notes</span>
              <textarea
                rows="4"
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                placeholder="Add private notes about this applicant (not visible to the applicant)..."
              />
              <button
                className="btn btn-primary"
                onClick={saveNotes}
                disabled={savingNotes}
              >
                {savingNotes ? "Saving..." : "Save Notes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
